<script lang="ts">
  import type { TabVariants } from './models';
  import { TabVariant } from './models';

  export * from './keys';
  export * from './models';

  export interface TabsProps {
    /**
     * The currently active tab value
     */
    activeTab: string;

    /**
     * Tabs variant
     */
    variant?: TabVariants;
  }
</script>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, onUpdated, provide, useTemplateRef, watch } from 'vue';

  import { t } from '../../locale';
  import { toElement } from '../../utils/templateRefNarrowing';
  import Icon from '../Icon/Icon.vue';
  import MoreActions from '../MoreActions/MoreActions.vue';
  import { TABS_INJECTION } from './keys';

  const emit = defineEmits<{
    (e: 'update:activeTab', newTabValue: TabsProps['activeTab']): void;
  }>();

  const props = withDefaults(defineProps<TabsProps>(), {
    variant: TabVariant.Line,
  });

  const currentActiveTab = computed({
    get() {
      return props.activeTab;
    },
    set(nv: TabsProps['activeTab']) {
      emit('update:activeTab', nv);
    },
  });

  function setActiveTab(newTabValue: TabsProps['activeTab']) {
    currentActiveTab.value = newTabValue;
  }

  provide(TABS_INJECTION.key, {
    activeTab: computed(() => currentActiveTab.value),
    variant: computed(() => props.variant),
    setActiveTab,
  });

  const moreDropdownMenuRef = useTemplateRef('moreDropdownMenuRef');
  const tabsContainerRef = useTemplateRef('tabsContainerRef');

  // Store event listeners to allow proper cleanup
  const eventListeners = new Map<Element, EventListener>();

  function cleanupMoreActionsHandlers() {
    eventListeners.forEach((listener, element) => {
      element.removeEventListener('click', listener);
    });
    eventListeners.clear();
  }

  // Setup automatic handlers for MenuItem elements in more-actions dropdown
  function setupMoreActionsHandlers() {
    const menuEl = toElement(moreDropdownMenuRef.value);
    if (!menuEl) return;

    // Remove existing listeners first to prevent duplication
    cleanupMoreActionsHandlers();

    const menuItems = menuEl.querySelectorAll('[data-action-id]');

    menuItems.forEach((item) => {
      const actionId = item.getAttribute('data-action-id');
      if (!actionId) return;

      // Update aria-selected based on active tab
      const isActive = currentActiveTab.value === actionId;
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');

      // Create and store the listener
      const listener = () => {
        // Find and click the original tab in the actions container
        const container = toElement(tabsContainerRef.value);
        if (container) {
          const originalTab = container.querySelector<HTMLElement>(`[role="tab"][data-action-id="${actionId}"]`);
          const firstChild = originalTab?.firstChild;
          if (firstChild && firstChild instanceof HTMLElement) {
            firstChild.click();
          }
        }
      };

      item.addEventListener('click', listener);
      eventListeners.set(item, listener);
    });
  }

  onMounted(() => {
    setupMoreActionsHandlers();
  });

  onUpdated(() => {
    setupMoreActionsHandlers();
  });

  watch(currentActiveTab, () => {
    setupMoreActionsHandlers();
  });

  onBeforeUnmount(() => {
    cleanupMoreActionsHandlers();
  });
</script>

<template>
  <div ref="tabsContainerRef" class="stash-tabs relative" role="tabList" data-test="stash-tabs">
    <template v-if="$slots['more-actions']">
      <MoreActions
        class="stash-tabs-list flex items-end"
        :class="{
          'stash-tabs-list--line': variant === 'line',
          'stash-tabs-list--enclosed': variant === 'enclosed',
          'gap-6': variant === 'line',
        }"
        :more-button-text="t('ll.more')"
        dropdown-mode="custom"
        :actions-container-class="{
          'gap-0': variant === 'enclosed',
          'gap-6': variant === 'line',
        }"
        actions-container-tag="ul"
      >
        <slot></slot>

        <template #more-actions>
          <div ref="moreDropdownMenuRef">
            <slot name="more-actions"></slot>
          </div>
        </template>

        <template #toggle="{ toggle, isOpen }">
          <button
            aria-haspopup="menu"
            :aria-expanded="isOpen"
            class="flex cursor-pointer items-center justify-center border-solid px-6 pt-1.5 pb-1 text-sm font-medium text-blue-500 hover:text-blue-700"
            :class="{ 'border-t-4 border-transparent': props.variant === 'enclosed' }"
            type="button"
            @click="toggle"
          >
            {{ t('ll.more') }}
            <Icon name="caret-down" />
          </button>
        </template>
      </MoreActions>
    </template>

    <template v-else>
      <ul
        class="stash-tabs-list flex items-end overflow-x-scroll"
        :class="{
          'stash-tabs-list--line': variant === 'line',
          'stash-tabs-list--enclosed': variant === 'enclosed',
          'gap-0': variant === 'enclosed',
          'gap-6': variant === 'line',
        }"
      >
        <slot></slot>
      </ul>
    </template>
  </div>
</template>
